import type { InferType } from 'yup';
import { currencyFormat } from '@/utils/currency';
import { toNumber } from '@/features/inventory/activity-pricing.utils';
import type { IGiftCardValue } from './gift-card-value.types';
import type { giftCardValueValidationSchema } from './gift-card-value.validations';

type GiftCardValueValues = InferType<typeof giftCardValueValidationSchema>;

/** `companyId` rides the create only; the update DTO does not declare it. */
const toGiftCardValuePayload = (
  values: GiftCardValueValues,
  { create }: { create: boolean }
) => ({
  ...(create ? { companyId: values.companyId } : {}),
  value: values.value,
  status: values.status,
});

/**
 * The quick picks offered until a company sets its own: the amounts the widget
 * and the CMS form always carried.
 */
const DEFAULT_GIFT_CARD_VALUES = [25, 50, 75, 100, 150];

interface IGiftCardValueOption {
  label: string;
  value: number;
}

const toGiftCardValueOption = (value: number): IGiftCardValueOption => ({
  label: currencyFormat({ amount: value }),
  value,
});

/**
 * A company's values as quick picks: numeric, positive, deduplicated and
 * smallest first. The defaults when it has none.
 */
const toGiftCardValueOptions = (
  rows: IGiftCardValue[] | undefined
): IGiftCardValueOption[] => {
  const values = [
    ...new Set(
      (rows ?? [])
        .map(row => toNumber(row.value))
        .filter((value): value is number => value !== undefined && value > 0)
    ),
  ].sort((a, b) => a - b);

  return (values.length > 0 ? values : DEFAULT_GIFT_CARD_VALUES).map(
    toGiftCardValueOption
  );
};

export {
  toGiftCardValuePayload,
  toGiftCardValueOptions,
  DEFAULT_GIFT_CARD_VALUES,
};
export type { GiftCardValueValues, IGiftCardValueOption };
